// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Hand-rolled SVG chart primitives. No chart library — see factory rules. */
import { escapeHtml, formatCompact } from '../utils/format';

export const SVG_NS = 'http://www.w3.org/2000/svg';

export function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

export function niceCeil(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(max));
  const norm = max / mag;
  let step: number;
  if (norm <= 1) step = 1;
  else if (norm <= 2) step = 2;
  else if (norm <= 2.5) step = 2.5;
  else if (norm <= 5) step = 5;
  else step = 10;
  return step * mag;
}

export function ticks(max: number, count = 5): number[] {
  const top = niceCeil(max);
  return Array.from({ length: count + 1 }, (_, i) => (top / count) * i);
}

// ── Horizontal ranked bars ────────────────────────────────────────────────────
export interface HBarDatum {
  id: string;
  label: string;
  value: number;
  colour: string;
  tip: string;
  flag?: string;
  sublabel?: string;
}

export function horizontalBars(
  data: HBarDatum[],
  opts: { width?: number; rowHeight?: number; labelWidth?: number; valueFormat?: (v: number) => string; onClick?: (id: string) => void } = {},
): SVGSVGElement {
  const width = opts.width ?? 900;
  const rowH = opts.rowHeight ?? 28;
  const labelW = opts.labelWidth ?? 220;
  const fmt = opts.valueFormat ?? ((v: number) => formatCompact(v));
  const valueW = 74;
  const barMax = Math.max(60, width - labelW - valueW - 16);
  const height = Math.max(1, data.length) * rowH + 8;
  const max = niceCeil(Math.max(...data.map((d) => d.value), 0.0001));

  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  data.forEach((d, i) => {
    const y = i * rowH + 4;
    const g = el('g');
    const label = el('text', { x: labelW - 8, y: y + rowH / 2, 'text-anchor': 'end', 'dominant-baseline': 'middle', 'font-size': 11.5, class: 'bar-label' });
    label.textContent = d.label;
    if (d.sublabel) {
      const t = el('title');
      t.textContent = `${d.label} · ${d.sublabel}`;
      label.appendChild(t);
    }
    g.appendChild(label);
    g.appendChild(el('rect', { x: labelW, y: y + 2, width: barMax, height: rowH - 8, rx: 3, fill: 'var(--bg-elevated)' }));
    const w = Math.max(1, (d.value / max) * barMax);
    const bar = el('rect', { class: 'bar', x: labelW, y: y + 2, width: w, height: rowH - 8, rx: 3, fill: d.colour, 'data-tip': d.tip, 'aria-label': d.tip, tabindex: 0, role: 'button' });
    g.appendChild(bar);
    const val = el('text', { class: 'val-label', x: labelW + barMax + 8, y: y + rowH / 2, 'dominant-baseline': 'middle' });
    val.textContent = fmt(d.value) + (d.flag ? ` ${d.flag}` : '');
    g.appendChild(val);
    const hit = el('rect', { x: 0, y, width, height: rowH, fill: 'transparent', style: 'cursor:pointer', 'data-tip': d.tip });
    g.appendChild(hit);
    if (opts.onClick) {
      const fire = () => opts.onClick!(d.id);
      g.addEventListener('click', fire);
      bar.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
          e.preventDefault();
          fire();
        }
      });
    }
    svg.appendChild(g);
  });
  return svg;
}

// ── Diverging bars around a zero line (net position over/under baseline) ───────
export interface DivDatum {
  id: string;
  label: string;
  value: number; // signed
  colourPos: string;
  colourNeg: string;
  tip: string;
  onClick?: boolean;
}

export function divergingBars(
  data: DivDatum[],
  opts: { width?: number; rowHeight?: number; labelWidth?: number; valueFormat?: (v: number) => string; onClick?: (id: string) => void } = {},
): SVGSVGElement {
  const width = opts.width ?? 900;
  const rowH = opts.rowHeight ?? 22;
  const labelW = opts.labelWidth ?? 210;
  const fmt = opts.valueFormat ?? ((v: number) => formatCompact(v));
  const height = Math.max(1, data.length) * rowH + 8;
  const maxAbs = Math.max(...data.map((d) => Math.abs(d.value)), 0.0001);
  const axisX = labelW + 8;
  const half = Math.max(30, (width - axisX - 70) / 2);
  const zero = axisX + half;
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  // Zero line.
  svg.appendChild(el('line', { x1: zero, y1: 2, x2: zero, y2: height - 6, stroke: 'var(--border-strong)', 'stroke-width': 1 }));

  data.forEach((d, i) => {
    const y = i * rowH + 4;
    const g = el('g');
    const label = el('text', { x: labelW, y: y + rowH / 2, 'text-anchor': 'end', 'dominant-baseline': 'middle', 'font-size': 11, class: 'bar-label' });
    label.textContent = d.label;
    g.appendChild(label);
    const w = (Math.abs(d.value) / maxAbs) * half;
    const positive = d.value >= 0;
    const x = positive ? zero : zero - w;
    const bar = el('rect', {
      class: 'bar',
      x,
      y: y + 2,
      width: Math.max(1, w),
      height: rowH - 6,
      rx: 2,
      fill: positive ? d.colourPos : d.colourNeg,
      'data-tip': d.tip,
      'aria-label': d.tip,
    });
    g.appendChild(bar);
    const val = el('text', {
      class: 'val-label',
      x: positive ? x + w + 6 : x - 6,
      y: y + rowH / 2,
      'text-anchor': positive ? 'start' : 'end',
      'dominant-baseline': 'middle',
    });
    val.textContent = (d.value > 0 ? '+' : '') + fmt(d.value);
    g.appendChild(val);
    const hit = el('rect', { x: 0, y, width, height: rowH, fill: 'transparent', style: 'cursor:pointer', 'data-tip': d.tip });
    g.appendChild(hit);
    if (opts.onClick) g.addEventListener('click', () => opts.onClick!(d.id));
    svg.appendChild(g);
  });
  return svg;
}

// ── Vertical column chart ──────────────────────────────────────────────────────
export interface ColumnDatum {
  id: string;
  label: string;
  value: number;
  colour: string;
  tip: string;
}
export interface ColumnOpts {
  width?: number;
  height?: number;
  labelEvery?: number;
  onClick?: (id: string) => void;
  valueFormat?: (v: number) => string;
}

export function columnChart(data: ColumnDatum[], opts: ColumnOpts = {}): SVGSVGElement {
  const width = opts.width ?? 960;
  const height = opts.height ?? 340;
  const m = { top: 18, right: 16, bottom: 60, left: 56 };
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;
  const max = niceCeil(Math.max(...data.map((d) => d.value), 0.0001));
  const fmt = opts.valueFormat ?? ((v: number) => formatCompact(v));
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  for (const t of ticks(max, 4)) {
    const y = m.top + plotH - (t / max) * plotH;
    svg.appendChild(el('line', { class: 'grid-line', x1: m.left, y1: y, x2: m.left + plotW, y2: y }));
    const lbl = el('text', { class: 'axis-label', x: m.left - 8, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    lbl.textContent = fmt(t);
    svg.appendChild(lbl);
  }
  const slot = plotW / Math.max(1, data.length);
  const barW = Math.max(1, slot * 0.72);
  const labelEvery = opts.labelEvery ?? 1;
  data.forEach((d, i) => {
    const x = m.left + i * slot + (slot - barW) / 2;
    const h = Math.max(0.5, (d.value / max) * plotH);
    const y = m.top + plotH - h;
    const bar = el('rect', { class: 'bar', x, y, width: barW, height: h, rx: Math.min(2, barW / 3), fill: d.colour, 'data-tip': d.tip, 'aria-label': d.tip });
    if (opts.onClick) {
      bar.style.cursor = 'pointer';
      bar.addEventListener('click', () => opts.onClick!(d.id));
    }
    svg.appendChild(bar);
    if (i % labelEvery === 0) {
      const lbl = el('text', { class: 'axis-label', x: x + barW / 2, y: height - m.bottom + 12, 'text-anchor': 'end', transform: `rotate(-35 ${x + barW / 2} ${height - m.bottom + 12})` });
      lbl.textContent = d.label;
      svg.appendChild(lbl);
    }
  });
  svg.appendChild(el('line', { class: 'axis-line', x1: m.left, y1: m.top + plotH, x2: m.left + plotW, y2: m.top + plotH }));
  return svg;
}

// ── Log-log scatter (baseline vs covered — the "over the line" signature) ──────
export interface ScatterPoint {
  id: string;
  x: number;
  y: number;
  r: number;
  colour: string;
  tip: string;
  label?: string;
}

export function scatterLogLog(
  points: ScatterPoint[],
  opts: { width?: number; height?: number; min?: number; max?: number; xTitle?: string; yTitle?: string; onClick?: (id: string) => void } = {},
): SVGSVGElement {
  const width = opts.width ?? 900;
  const height = opts.height ?? 620;
  const m = { top: 20, right: 20, bottom: 52, left: 60 };
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;
  const vals = points.flatMap((p) => [p.x, p.y]).filter((v) => v > 0);
  const min = opts.min ?? Math.max(1000, Math.min(...vals) * 0.8);
  const max = opts.max ?? Math.max(...vals) * 1.15;
  const lmin = Math.log10(min);
  const lmax = Math.log10(max);
  const sx = (v: number) => m.left + ((Math.log10(Math.max(v, min)) - lmin) / (lmax - lmin)) * plotW;
  const sy = (v: number) => m.top + plotH - ((Math.log10(Math.max(v, min)) - lmin) / (lmax - lmin)) * plotH;

  const svg = el('svg', { class: 'chart scatter', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  // Decade grid.
  const decadeStart = Math.ceil(lmin);
  const decadeEnd = Math.floor(lmax);
  const fmtDec = (p: number) => {
    const v = 10 ** p;
    if (v >= 1e6) return `${v / 1e6}M`;
    if (v >= 1e3) return `${v / 1e3}k`;
    return String(v);
  };
  for (let p = decadeStart; p <= decadeEnd; p++) {
    const gx = sx(10 ** p);
    const gy = sy(10 ** p);
    svg.appendChild(el('line', { class: 'grid-line', x1: gx, y1: m.top, x2: gx, y2: m.top + plotH }));
    svg.appendChild(el('line', { class: 'grid-line', x1: m.left, y1: gy, x2: m.left + plotW, y2: gy }));
    const xl = el('text', { class: 'axis-label', x: gx, y: height - m.bottom + 14, 'text-anchor': 'middle' });
    xl.textContent = fmtDec(p);
    svg.appendChild(xl);
    const yl = el('text', { class: 'axis-label', x: m.left - 8, y: gy, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    yl.textContent = fmtDec(p);
    svg.appendChild(yl);
  }

  // y = x diagonal (at-baseline line): covered == baseline.
  svg.appendChild(el('line', { x1: sx(min), y1: sy(min), x2: sx(max), y2: sy(max), stroke: 'var(--text-secondary)', 'stroke-width': 1.4, 'stroke-dasharray': '5 4', opacity: 0.75 }));
  const diagLbl = el('text', { x: sx(max) - 6, y: sy(max) - 8, 'text-anchor': 'end', 'font-size': 11, 'font-weight': 600, fill: 'var(--text-secondary)' });
  diagLbl.textContent = 'at baseline';
  svg.appendChild(diagLbl);

  // Axis titles.
  const xt = el('text', { x: m.left + plotW / 2, y: height - 8, 'text-anchor': 'middle', 'font-size': 11.5, 'font-weight': 600, fill: 'var(--text-secondary)' });
  xt.textContent = opts.xTitle ?? '';
  svg.appendChild(xt);
  const yt = el('text', { x: 14, y: m.top + plotH / 2, 'text-anchor': 'middle', 'font-size': 11.5, 'font-weight': 600, fill: 'var(--text-secondary)', transform: `rotate(-90 14 ${m.top + plotH / 2})` });
  yt.textContent = opts.yTitle ?? '';
  svg.appendChild(yt);

  const layer = el('g', { class: 'scatter-points' });
  for (const p of points) {
    const dot = el('circle', {
      cx: sx(p.x),
      cy: sy(p.y),
      r: p.r,
      fill: p.colour,
      'fill-opacity': 0.7,
      stroke: '#fff',
      'stroke-width': 0.8,
      'data-tip': p.tip,
      'aria-label': p.tip,
      style: opts.onClick ? 'cursor:pointer' : '',
    });
    if (opts.onClick) dot.addEventListener('click', () => opts.onClick!(p.id));
    layer.appendChild(dot);
  }
  svg.appendChild(layer);
  return svg;
}

// ── Multi-line chart ───────────────────────────────────────────────────────────
export interface LineSeries {
  id: string;
  label: string;
  colour: string;
  values: Array<number | null>;
}

export function multiLine(
  xLabels: Array<string | number>,
  series: LineSeries[],
  opts: { width?: number; height?: number; yMax?: number; yFmt?: (v: number) => string } = {},
): SVGSVGElement {
  const width = opts.width ?? 960;
  const height = opts.height ?? 340;
  const m = { top: 16, right: 110, bottom: 34, left: 52 };
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;
  const n = xLabels.length;
  const allVals = series.flatMap((s) => s.values.filter((v): v is number => v != null));
  const yMax = opts.yMax ?? niceCeil(Math.max(...allVals, 0.0001));
  const yFmt = opts.yFmt ?? ((v: number) => formatCompact(v));
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  const xAt = (i: number) => m.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yAt = (v: number) => m.top + plotH - (v / yMax) * plotH;

  for (const t of ticks(yMax, 4)) {
    const y = yAt(t);
    svg.appendChild(el('line', { class: 'grid-line', x1: m.left, y1: y, x2: m.left + plotW, y2: y }));
    const lbl = el('text', { class: 'axis-label', x: m.left - 6, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    lbl.textContent = yFmt(t);
    svg.appendChild(lbl);
  }
  xLabels.forEach((xl, i) => {
    const lbl = el('text', { class: 'axis-label', x: xAt(i), y: height - m.bottom + 14, 'text-anchor': 'middle' });
    lbl.textContent = String(xl);
    svg.appendChild(lbl);
  });
  svg.appendChild(el('line', { class: 'axis-line', x1: m.left, y1: m.top + plotH, x2: m.left + plotW, y2: m.top + plotH }));

  for (const s of series) {
    const pts = s.values.map((v, i) => (v == null ? null : `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`)).filter((p): p is string => p != null);
    if (pts.length === 0) continue;
    svg.appendChild(el('polyline', { points: pts.join(' '), fill: 'none', stroke: s.colour, 'stroke-width': 2.2, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }));
    s.values.forEach((v, i) => {
      if (v == null) return;
      svg.appendChild(el('circle', { cx: xAt(i), cy: yAt(v), r: 3, fill: s.colour, 'data-tip': `${s.label} · ${xLabels[i]}: ${yFmt(v)}` }));
    });
    const lastIdx = s.values.map((v, i) => (v != null ? i : -1)).filter((i) => i >= 0).pop();
    if (lastIdx != null) {
      const lbl = el('text', { x: xAt(lastIdx) + 8, y: yAt(s.values[lastIdx] as number), 'dominant-baseline': 'middle', 'font-size': 11, 'font-weight': 600, fill: s.colour });
      lbl.textContent = s.label;
      svg.appendChild(lbl);
    }
  }
  return svg;
}

// ── Donut ──────────────────────────────────────────────────────────────────────
export interface DonutSeg {
  label: string;
  value: number;
  colour: string;
}
export function donut(segs: DonutSeg[], opts: { size?: number } = {}): SVGSVGElement {
  const size = opts.size ?? 200;
  const r = size / 2;
  const inner = r * 0.58;
  const cx = r;
  const cy = r;
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${size} ${size}`, width: size, height: size, role: 'img' });
  const total = segs.reduce((s, x) => s + x.value, 0) || 1;
  let a0 = -Math.PI / 2;
  for (const seg of segs) {
    const frac = seg.value / total;
    if (frac <= 0) continue;
    const a1 = a0 + frac * Math.PI * 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    const xi0 = cx + inner * Math.cos(a1);
    const yi0 = cy + inner * Math.sin(a1);
    const xi1 = cx + inner * Math.cos(a0);
    const yi1 = cy + inner * Math.sin(a0);
    const d = `M${x0.toFixed(2)},${y0.toFixed(2)} A${r},${r} 0 ${large} 1 ${x1.toFixed(2)},${y1.toFixed(2)} L${xi0.toFixed(2)},${yi0.toFixed(2)} A${inner},${inner} 0 ${large} 0 ${xi1.toFixed(2)},${yi1.toFixed(2)} Z`;
    svg.appendChild(el('path', { d, fill: seg.colour, stroke: 'var(--bg-panel)', 'stroke-width': 1.5, 'data-tip': `${seg.label}: ${(frac * 100).toFixed(1)}% (${Math.round(seg.value).toLocaleString()})`, style: 'cursor:default' }));
    a0 = a1;
  }
  return svg;
}

// ── Sequential ramp for the choropleth (carbon ember) ──────────────────────────
export const RAMP = ['#fde9d2', '#fdd0a2', '#fdae6b', '#fd8d3c', '#f16913', '#d94801', '#8c2d04'];

export function rampColour(value: number | null, stops: number[]): string {
  if (value == null || !Number.isFinite(value)) return '#e8ecef';
  for (let i = 0; i < stops.length; i++) {
    if (value < stops[i]) return RAMP[i];
  }
  return RAMP[RAMP.length - 1];
}

export { escapeHtml };
